import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Filled_Star from "../assets/Filled Star.png";
import allDishesDataRaw from "../assets/dishes2.json";
import Star from "../Component/Star.jsx";

// BASE URL is used ONLY for constructing URLs from the upload API response.
const IMAGE_BASE_URL = "https://webdev.cse.buffalo.edu";
const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const serverBaseURL = "https://webdev.cse.buffalo.edu/hci/teams/indecisive";

const PostForm = ({ refresh, loadPosts }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- State Variables ---
  const [postTitle, setPostTitle] = useState("");
  const [postText, setPostText] = useState("");
  const [postImage, setPostImage] = useState("");
  const [userManuallySetImage, setUserManuallySetImage] = useState(false); // <-- New State Flag
  const [allDishesData, setAllDishesData] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [selectedDishId, setSelectedDishId] = useState("");
  const [selectedDish, setSelectedDish] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [postMessage, setPostMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [privatePost, setPrivatePost] = useState(false);
  const [tags, setTags]=useState([]);
  const [preFilledMessage, setPreFilledMessage] = useState("");

  // Add new state for form validation
  const [isFormValid, setIsFormValid] = useState(false);

  // --- Constants ---
  const MAX_FILE_SIZE = 1024 * 1024; // 1MB limit
  const MAX_REVIEW_WORDS = 250;
  const handleRatingChange = (newRating) => {
    setRating(newRating); // Update rating on click
  };
  
  // Check if user is logged in and redirect if not (fallback protection)
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("user");
    
    if (!token || !userId) {
      // User is not logged in, redirect to login page
      console.log("User not logged in, redirecting to login page");
      
      // Get the current path to store it in redirect state
      const currentPath = window.location.pathname;
      
      // Use a small timeout to avoid race conditions with the ProtectedRoute component
      const redirectTimer = setTimeout(() => {
        navigate("/login", { 
          state: { 
            from: currentPath,
            message: "Please log in to add a review" 
          } 
        });
      }, 100);
      
      // Clean up timer if component unmounts
      return () => clearTimeout(redirectTimer);
    }
  }, [navigate]);

  // --- Load Dish Data ---
  useEffect(() => {
    setIsLoading(true);
    try {
      if (!Array.isArray(allDishesDataRaw)) {
        console.error("Imported data is not an array:", allDishesDataRaw);
        throw new Error("Invalid data format in imported dishes JSON.");
      }
      setAllDishesData(allDishesDataRaw);
      const uniqueRestaurants = [
        ...new Set(allDishesDataRaw.map((dish) => dish.restaurantName))
      ].sort();
      setRestaurants(uniqueRestaurants);
      setIsLoading(false);
    } catch (error) {
      console.error("Error processing imported dishes data:", error);
      setErrorMessage(`Failed to process dish data. ${error.message}`);
      setIsLoading(false);
    }
  }, []);

  // Pre-select restaurant and dish from location state if provided
  useEffect(() => {
    // Check if we have dish and restaurant info in location state
    if (location.state && location.state.restaurantName && location.state.dishId && allDishesData.length > 0) {
      const { restaurantName, dishId, dishTitle } = location.state;
      
      console.log(`[PostForm] Preselecting restaurant: ${restaurantName} and dish: ${dishId}`);
      
      // Set the restaurant first
      setSelectedRestaurant(restaurantName);
      
      // Filter dishes for this restaurant
      const dishes = allDishesData.filter(
        (dish) => dish.restaurantName === restaurantName
      );
      setFilteredDishes(dishes);
      
      // Find and set the dish
      const dishIdStr = String(dishId);
      const dish = allDishesData.find(d => String(d.id) === dishIdStr);
      
      if (dish) {
        setSelectedDishId(dishIdStr);
        setSelectedDish(dish);
        setTags(dish.tags);
        setPostTitle(dish.title || "");
        
        // Set dish image if available
        if (!userManuallySetImage && dish.image) {
          const imagePathFromJson = dish.image || "";
          const fullImageUrl = imagePathFromJson.startsWith("/")
            ? (isLocalhost
                ? imagePathFromJson
                : `${serverBaseURL}${imagePathFromJson}`)
            : imagePathFromJson;
        
          setPostImage(fullImageUrl);
        }
        
        // Set a helpful message
        setPreFilledMessage(`Form pre-filled with "${dishTitle || dish.title}" from ${restaurantName}`);
      }
    }
  }, [location.state, allDishesData, isLocalhost, userManuallySetImage]);

  // Validate form whenever required fields change
  useEffect(() => {
    const validateForm = () => {
      const isValid = 
        selectedRestaurant !== "" && // Restaurant is selected
        selectedDishId !== "" &&     // Dish is selected
        postTitle.trim() !== "" &&   // Title is not empty
        postText.trim() !== "" &&    // Review text is not empty
        rating > 0 &&                // Rating is set
        // Check word count
        postText.split(/\s+/).filter(Boolean).length <= MAX_REVIEW_WORDS;
      
      setIsFormValid(isValid);
    };

    validateForm();
  }, [selectedRestaurant, selectedDishId, postTitle, postText, rating]);

  // --- Debugging: Log image state changes ---
  // useEffect(() => {
  //   console.log("[PostForm Debug] postImage state updated to:", postImage);
  //   console.log("[PostForm Debug] userManuallySetImage state:", userManuallySetImage);
  // }, [postImage, userManuallySetImage]); // Log both

  // --- Handler: Restaurant Dropdown Change ---
  const handleRestaurantChange = (event) => {
    const restaurantName = event.target.value;
    setSelectedRestaurant(restaurantName);
    // Reset dish and form fields, INCLUDING image manual flag
    setSelectedDishId("");
    setSelectedDish(null);
    setFilteredDishes([]);
    setPostTitle("");
    setPostText("");
    setPostImage("");
    setTags([]);
    setUserManuallySetImage(false); // <-- Reset flag when restaurant changes
    setErrorMessage("");

    if (restaurantName) {
      setIsLoading(true);
      const dishes = allDishesData.filter(
        (dish) => dish.restaurantName === restaurantName
      );
      setFilteredDishes(dishes);
      // Removed setTimeout for loading state
      setIsLoading(false);
    }
  };

  // --- Handler: Dish Dropdown Change ---
  const handleDishChange = (event) => {
    const dishId = event.target.value;
    setSelectedDishId(dishId);
    setErrorMessage("");
    console.log(`[PostForm Debug] Dish ID selected: ${dishId}`);

    if (dishId) {
      const dish = allDishesData.find(
        (d) => String(d.id) === String(dishId)
      );
      if (dish) {
        console.log("[PostForm Debug] Found dish object:", dish);
        setSelectedDish(dish);
        setTags(dish.tags || []);

        // Auto-populate the title from the dish
        setPostTitle(dish.title || "");
        // NOTE: The postText (user's review field) is NOT auto-populated here.

        // --- MODIFIED IMAGE LOGIC ---
        // Only set the default image if the user hasn't already uploaded/cleared one
        if (!userManuallySetImage) {
          const imagePathFromJson = dish.image || "";

          const fullImageUrl = imagePathFromJson.startsWith("/")
            ? (isLocalhost
                ? imagePathFromJson
                : `${serverBaseURL}${imagePathFromJson}`)
            : imagePathFromJson;
        
          setPostImage(fullImageUrl);
        }
        else {
            console.log("[PostForm Debug] User image/choice exists, not overwriting with default.");

        }
        // --- END MODIFIED IMAGE LOGIC ---

      } else {
        console.error(`[PostForm Debug] Dish not found in allDishesData for ID: ${dishId}`);
        setSelectedDish(null);
        setTags([]);
        setPostTitle("");
        // Don't clear postImage here automatically, respect userManuallySetImage
        if (!userManuallySetImage) {
          setPostImage("");
        }
      }
    } else {
      console.log("[PostForm Debug] Dish selection cleared.");
      setSelectedDish(null);
      setPostTitle("");
      // Don't clear postImage here automatically, respect userManuallySetImage
      if (!userManuallySetImage) {
        setPostImage("");
      }
    }
  };

  // --- Handler: Title Input Change (Editable) ---
  const handleTitleInputChange = (event) => {
    setPostTitle(event.target.value);
  };

  // --- Handler: Manual Image Upload ---
  const uploadPicture = (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      setErrorMessage("Only image files (JPEG, PNG, GIF, etc.) are allowed.");
      event.target.value = null;
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(`File size exceeds the limit of ${MAX_FILE_SIZE / 1024 / 1024}MB.`);
      event.target.value = null;
      return;
    }
    // Removed warning, behavior is now clearer

    const formData = new FormData();
    formData.append("uploaderID", sessionStorage.getItem("user"));
    formData.append("attributes", JSON.stringify({ context: "review_image" }));
    formData.append("file", file);
    setErrorMessage("");

    // Upload the file via API
    fetch(process.env.REACT_APP_API_PATH + "/file-uploads", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + sessionStorage.getItem("token")
      },
      body: formData
    })
      .then((res) => res.json())
      .then((result) => {
        if (result && result.path) {
          const pictureURL = IMAGE_BASE_URL + result.path;
          console.log("[PostForm Debug] Setting postImage from UPLOAD:", pictureURL);
          setPostImage(pictureURL);
          setUserManuallySetImage(true); // <-- SET FLAG on successful upload
          setErrorMessage("");
          event.target.value = null; // Clear file input after successful upload
        } else {
          throw new Error(result?.message || "Upload failed, path not received.");
        }
      })
      .catch((error) => {
        console.error("Image Upload Error:", error);
        setErrorMessage("Error uploading image. Please try again.");
        // Don't clear file input on error, user might want to retry
      });
  };

  // --- Handler: Clear User Image ---
  const clearUserImage = () => {
    setPostImage("");
    setUserManuallySetImage(false); // <-- RESET FLAG when clearing
    // Clear the file input visually if possible (can be tricky across browsers)
    const fileInput = document.getElementById('post-image-upload') || document.getElementById('post-image-upload-change');
    if (fileInput) {
      fileInput.value = null;
    }
    console.log("[PostForm Debug] User cleared image.");
  };


  // --- Handler: Review Text Change with Word Count Validation ---
  const handleReviewChange = (e) => {
    const text = e.target.value;
    const words = text.split(/\s+/).filter(Boolean);
    setPostText(text);

    if (words.length > MAX_REVIEW_WORDS) {
      setErrorMessage(`Review cannot exceed ${MAX_REVIEW_WORDS} words.`);
    } else {
      setErrorMessage("");
    }
  };

  // --- Submit Handler ---
  const submitHandler = (event) => {
    event.preventDefault();
    setErrorMessage("");

    // --- Form Validation ---
    if (!selectedRestaurant) {
      setErrorMessage("Please select a restaurant.");
      return;
    }
    if (!selectedDishId) {
      setErrorMessage("Please select a dish.");
      return;
    }
    if (postTitle.trim() === "") {
      setErrorMessage("Please ensure the post has a title.");
      return;
    }
    if (postText.trim() === "") {
      setErrorMessage("Please enter your review.");
      return;
    }
    const reviewWords = postText.trim().split(/\s+/).filter(Boolean);
    if (reviewWords.length > MAX_REVIEW_WORDS) {
      setErrorMessage(`Your review must NOT exceed ${MAX_REVIEW_WORDS} words (currently ${reviewWords.length}).`);
      return;
    }
    // --- End Validation ---

    // --- API Call to Create Post ---
    fetch(process.env.REACT_APP_API_PATH + "/posts", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + sessionStorage.getItem("token")
      },
      body: JSON.stringify({
        authorID: sessionStorage.getItem("user"),
        content: postText,
        attributes: {
          title: postTitle,
          image: postImage, // Send the current postImage (could be default, uploaded, or empty)
          private: privatePost,
          dishID: selectedDishId,
          restaurantName: selectedRestaurant,
          // Add review-specific metadata
          isReview: true,
          tag:tags,
          overallRating: rating,
          review: postText,
          restaurant: selectedRestaurant

        }
      })
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result && result.id) {
            if (typeof loadPosts === "function") {
          loadPosts();
            }
            // Reset form state on successful submission
          setPostText("");
          setPostTitle("");
          setPostImage("");
          setTags([]);
            setRating(0);
            setUserManuallySetImage(false); // <-- Reset flag on success
            setSelectedRestaurant("");
            setSelectedDishId("");
            setSelectedDish(null);
            setFilteredDishes([]);
          setErrorMessage(""); 
            setPostMessage("Review Added Successfully!");
            setTimeout(() => setPostMessage(""), 3000);
            navigate(`/reviewPost/${result.id}`);
          } else {
            console.error("API Error Response:", result);
            setErrorMessage(result?.message || "Error Submitting Post. API did not return success.");
          }
        },
        (error) => {
          console.error("Fetch Error:", error);
          setErrorMessage("Error Submitting Post. Please check your connection and try again.");
        }
      );
  };

  // --- Popup Component ---
  const Popup = ({ message, onClose }) => {
    if (!message) return null;
    return (
      <div className="popup">
        <div className="popup-content">
          <p>{message}</p>
          <button className="close-button" onClick={onClose}>
            Okay
          </button>
        </div>
      </div>
    );
  };

  // --- Component Render ---
  return (
    <div className="entire-PostForm">
       <Popup message={errorMessage} onClose={() => setErrorMessage("")} />
      {/* Removed <head> section - place font links in public/index.html */}
      <h2 className="post-view-title">Adding a Review</h2> {/* Add this line */}
      
      {/* Pre-filled notification */}
      {preFilledMessage && (
        <div className="pre-filled-message" style={{
          backgroundColor: "#e6f7ff", 
          color: "#0070f3", 
          padding: "10px 15px",
          borderRadius: "5px",
          marginBottom: "15px",
          border: "1px solid #b3e0ff",
          textAlign: "center",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <span style={{ marginRight: "8px" }}>ℹ️</span>
          {preFilledMessage}
        </div>
      )}
      
      <form className="together" onSubmit={submitHandler}>
        <div className="PostForm">
          {/* <label className="privacy-label">
            <input
              type="checkbox"
              checked={privatePost}
              onChange={() => setPrivatePost(!privatePost)}
            />
            {privatePost ? "Private (Only Followers)" : "Public (Everyone)"}
          </label> */}
          {/* --- Restaurant Dropdown --- */}
          <label htmlFor="restaurant-select">
            Select Restaurant <span className="required">*</span>
          </label>
          <select
            id="restaurant-select"
            className="DropdownSelect RestaurantDropdown"
            value={selectedRestaurant}
            onChange={handleRestaurantChange}
            required
            aria-required="true"
          >
            <option value="">-- Select a Restaurant --</option>
            {restaurants.map((restaurantName) => (
              <option key={restaurantName} value={restaurantName}>
                {restaurantName}
              </option>
            ))}
          </select>
          {isLoading && !allDishesData.length && <p>Loading restaurants...</p>}

          {/* --- Dish Dropdown --- */}
          <label htmlFor="dish-select">
            Select Dish <span className="required">*</span>
          </label>
          <select
            id="dish-select"
            className="DropdownSelect DishDropdown"
            value={selectedDishId}
            onChange={handleDishChange}
            disabled={!selectedRestaurant || isLoading}
            required
            aria-required="true"
          >
            <option value="">-- Select a Dish --</option>
            {filteredDishes.map((dish) => (
              <option key={dish.id} value={String(dish.id)}>
                {dish.title}
              </option>
            ))}
          </select>
          {isLoading && selectedRestaurant && <p>Loading dishes...</p>}

          {/* --- Title Input (Editable) --- */}
          <label htmlFor="post-title">
            Title <span className="required">*</span>
          </label>
          <input
            id="post-title"
            className="Title"
          type="text"
            placeholder="Enter title (or select dish)"
          value={postTitle}
            onChange={handleTitleInputChange}
            required
          />

          {/* --- Image Upload / Preview Area --- */}
          <label htmlFor="post-image-upload">Review Image (Optional)</label>
          {!postImage ? (
            <div>
            <label for="post-image-upload" className="WithoutImage">
            Upload Image
          </label>
           
            <input
              id="post-image-upload"
            
            type="file"
              accept="image/*"
            onChange={uploadPicture}
          />
          </div>
          ) : (
            // Show preview, clear button, and change button if image is set
            <div>
              <img
                src={postImage}
                alt={selectedDish ? selectedDish.title : "Review image preview"}
                className="PostingImage"
                onError={(e) => {
                  console.error(`[PostForm Error] Failed to load image in preview. SRC: ${e.target.src}`);
                  // Optional: Set a placeholder image on error or clear
                  // setPostImage("");
                  // setUserManuallySetImage(false); // Reset if the image itself breaks
                }}
              />
              <button
                type="button"
                className="img-button"
                onClick={clearUserImage} // <-- Use dedicated handler
              >
                Clear Image
              </button>
              {/* Optionally, provide a way to change the image directly */}
              <label for="post-image-upload-change" className="WithoutImage" style={{ display: 'block', marginTop: '10px', fontSize: '0.8em' }}>
                Change Image
              </label>
              <input
                id="post-image-upload-change"
                
                type="file"
                accept="image/*"
                onChange={uploadPicture}
                 // Simpler than adding marginTop here
              />
            </div>
          )}

          {/* --- Rating Input --- */}
          <div className="rating" style={{ marginTop: "1rem", marginBottom: "1rem" }}>
            <label className="right" style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontWeight: 500
            }}>
              Rating <span className="required">*</span>
            </label>
            <Star rating={rating} onRatingChange={handleRatingChange} />
          </div>


          {/* --- Review Text Area --- */}
          <label htmlFor="post-review">
            Your Review <span className="required">*</span>
        </label>
          <textarea
            id="post-review"
            className="CommentBox"
            placeholder="Enter your review here."
          value={postText}
            rows="8"
          cols="70"
            onChange={handleReviewChange}
            required
            aria-required="true"
          />
          <p className="WordCount">
            {postText.split(/\s+/).filter(Boolean).length} / {MAX_REVIEW_WORDS} words
          </p>


          {/* --- Submit Button --- */}
          <button 
            className={`submit ${!isFormValid ? 'submit-disabled' : ''}`}
            type="submit" 
            disabled={!isFormValid || isLoading}
          >
            Submit Review
          </button>
          {postMessage && <p className="PostSuccessMessage">{postMessage}</p>}
        </div>

        {/* --- Information Panel --- */}
        {/* ... (information panel remains the same) ... */}
        <div className="Information">

          <h1>{selectedDish ? selectedDish.restaurantName : "Restaurant"}</h1>
          <h2>{selectedDish ? selectedDish.title : "Food Item"}</h2>
          {selectedDish?.tags?.length > 0 && (
            <>
              <h2>Categories/Tags</h2>
              <p>{selectedDish.tags.join(", ")}</p>
            </>
          )}
          <h2>Description</h2>
          <p style={{ whiteSpace: "pre-wrap" }}>
            {selectedDish ? selectedDish.description : "Select a dish to see its description."}
          </p>

          {/* Fixed size image container that's always visible */}
          <div style={{
            height: "350px",
            marginTop: "20px",
            marginRight: "15px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column"
          }}>
            <h2 style={{ marginBottom: "10px" }}>Image from Menu</h2>
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: selectedDish?.image ? "transparent" : "#f0f0f0",
              borderRadius: "4px",
              padding: "10px",
              overflow: "hidden"
            }}>
              {selectedDish?.image ? (
                <img
                  src={
                    selectedDish?.image?.startsWith("/")
                      ? (isLocalhost
                          ? selectedDish.image
                          : `${serverBaseURL}${selectedDish.image}`)
                      : selectedDish?.image
                  }
                  alt={selectedDish.title}
                  style={{
                    maxWidth: "90%",
                    maxHeight: "250px",
                    objectFit: "contain",
                    borderRadius: "4px"
                  }}
                  onError={(e) => {
                    console.error(`[PostForm Error] Failed to load image in info panel. SRC: ${e.target.src}`);
                  }}
                />
              ) : (
                <p style={{ color: "#888", fontStyle: "italic", textAlign: "center" }}>
                  Select a dish to see its image
                </p>
              )}
            </div>
          </div>
            </div>
    </form>
    </div>
  );
};

// Add styles to the existing CSS
const styles = `
  .required {
    color: #e53935;
    margin-left: 2px;
  }

  .submit-disabled {
    background-color: #cccccc !important;
    cursor: not-allowed !important;
    opacity: 0.7;
  }

  /* Add this if you want to show a tooltip on hover over disabled button */
  .submit-disabled:hover::after {
    content: "Please fill in all required fields";
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    margin-top: -30px;
    white-space: nowrap;
  }
`;

// Insert styles into document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default PostForm;
